import type {
  User, Game, Frame, WaitingEntry, AppSettings, Session,
  Table, TableStatus, TableType, GameType,
  Tournament, TournamentDetail, TournamentFormat,
  PricingSlot,
} from '../engine/types';

const BASE = '/api';
const TOKEN_KEY = 'cuetitan_token';

export function getToken(): string | null {
  return localStorage.getItem(TOKEN_KEY);
}

export function storeToken(token: string): void {
  localStorage.setItem(TOKEN_KEY, token);
}

export function clearToken(): void {
  localStorage.removeItem(TOKEN_KEY);
}

async function request<T>(method: string, path: string, body?: unknown): Promise<T> {
  const token = getToken();
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  if (token) headers['Authorization'] = `Bearer ${token}`;

  const res = await fetch(BASE + path, {
    method,
    headers,
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });

  if (res.status === 401) {
    clearToken();
    try { localStorage.removeItem('cuetitan_auth'); } catch { /* ignore */ }
    window.location.href = '/';
    throw new Error('Session expired');
  }

  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: res.statusText }));
    throw new Error((err as { error?: string }).error || res.statusText);
  }

  return res.json() as Promise<T>;
}

// ── Auth ────────────────────────────────────────────────────────────────────

export type LoginResult =
  | { result: 'ok'; token: string; user: Omit<User, 'pinHash'>; sessionId: number }
  | { result: 'must_change'; token: string; user: Omit<User, 'pinHash'>; sessionId: number };

export const auth = {
  getUsers: () => request<Omit<User, 'pinHash'>[]>('GET', '/auth/users'),

  login: (userId: number, pin: string) =>
    request<LoginResult>('POST', '/auth/login', { userId, pin }),

  logout: () =>
    request<{ ok: boolean; summary: { gamesCount: number; totalRevenue: number; durationMinutes: number } | null }>(
      'POST', '/auth/logout'
    ),

  changePin: (userId: number, newPin: string) =>
    request<{ token?: string; user: Omit<User, 'pinHash'>; sessionId?: number }>(
      'PUT', '/auth/pin', { userId, newPin }
    ),

  createUser: (data: {
    name: string; role: string; pin: string;
    managerId?: number; mustChangePin?: boolean;
  }) => request<Omit<User, 'pinHash'>>('POST', '/auth/users', data),
};

// ── Users ───────────────────────────────────────────────────────────────────

export const users = {
  getAll: () => request<Omit<User, 'pinHash'>[]>('GET', '/users'),
  getById: (id: number) => request<Omit<User, 'pinHash'>>('GET', `/users/${id}`),
  delete: (id: number) => request<{ ok: boolean }>('DELETE', `/users/${id}`),
  resetPin: (id: number, newPin: string) =>
    request<{ ok: boolean }>('PUT', `/users/${id}/pin`, { newPin }),
  getTableAssignments: (id: number) =>
    request<number[]>('GET', `/users/${id}/tables`),
  setTableAssignments: (id: number, tableIds: number[]) =>
    request<{ ok: boolean }>('PUT', `/users/${id}/tables`, { tableIds }),
};

// ── Games ───────────────────────────────────────────────────────────────────

export const games = {
  create: (data: {
    player1Name: string; player2Name: string; staffId: number;
    tableId?: number | null; gameType?: GameType;
    framesTarget: number; pricePerFrameSnapshot: number; currencySnapshot: string;
  }) => request<Game>('POST', '/games', data),

  getAll: (filter?: {
    staffId?: number; tableId?: number; fromDate?: string; toDate?: string;
    playerName?: string; status?: string;
  }) => {
    const params = new URLSearchParams();
    if (filter?.staffId) params.set('staffId', String(filter.staffId));
    if (filter?.tableId) params.set('tableId', String(filter.tableId));
    if (filter?.fromDate) params.set('fromDate', filter.fromDate);
    if (filter?.toDate) params.set('toDate', filter.toDate);
    if (filter?.playerName) params.set('playerName', filter.playerName);
    if (filter?.status) params.set('status', filter.status);
    const qs = params.toString();
    return request<Game[]>('GET', `/games${qs ? `?${qs}` : ''}`);
  },

  updateFrame: (gameId: number, opts: { player1Frames: number; player2Frames: number; framesPlayed: number }) =>
    request<{ ok: boolean }>('PUT', `/games/${gameId}/frame`, opts),

  updateLiveScore: (
    gameId: number,
    player1Score: number, player2Score: number,
    redsRemaining: number, currentPhase: 'reds' | 'colors', activePlayer: 0 | 1
  ) =>
    request<{ ok: boolean }>('PATCH', `/games/${gameId}/live-score`,
      { player1Score, player2Score, redsRemaining, currentPhase, activePlayer }),

  complete: (gameId: number, opts: { winnerName: string; loserName: string; amountOwed: number }) =>
    request<{ ok: boolean }>('PUT', `/games/${gameId}/complete`, opts),

  abandon: (gameId: number) =>
    request<{ ok: boolean }>('PUT', `/games/${gameId}/abandon`, {}),

  updatePlayerName: (gameId: number, playerIndex: 0 | 1, name: string) =>
    request<{ ok: boolean }>('PATCH', `/games/${gameId}/player-name`, { playerIndex, name }),

  purgeOld: (before: string) =>
    request<{ deleted: number }>('DELETE', `/games/old?before=${encodeURIComponent(before)}`),
};

// ── Tables ────────────────────────────────────────────────────────────────────

export const tables = {
  getAll: () => request<Table[]>('GET', '/tables'),
  getById: (id: number) => request<Table>('GET', `/tables/${id}`),
  getActiveGame: (tableId: number) => request<Game | null>('GET', `/tables/${tableId}/active-game`),
  create: (data: { name: string; type: TableType; sortOrder?: number }) =>
    request<Table>('POST', '/tables', data),
  update: (id: number, data: { name?: string; type?: TableType; status?: TableStatus; sortOrder?: number }) =>
    request<Table>('PUT', `/tables/${id}`, data),
  delete: (id: number) => request<{ ok: boolean }>('DELETE', `/tables/${id}`),
  getHistory: (tableId: number) => request<Game[]>('GET', `/tables/${tableId}/history`),
};

// ── Frames ──────────────────────────────────────────────────────────────────

export const frames = {
  create: (data: {
    gameId: number; frameNumber: number; winnerName: string;
    player1Score: number; player2Score: number;
    highestBreak?: number; highestBreakPlayer?: string; durationSeconds?: number;
    player1Balls?: number | null; player2Balls?: number | null;
  }) => request<Frame>('POST', '/frames', data),

  getByGame: (gameId: number) => request<Frame[]>('GET', `/frames?gameId=${gameId}`),

  getByDateRange: (fromDate: string, toDate: string) =>
    request<Frame[]>('GET', `/frames?fromDate=${fromDate}&toDate=${toDate}`),
};

// ── Settings ─────────────────────────────────────────────────────────────────

export const settings = {
  getAll: () => request<AppSettings>('GET', '/settings'),
  update: (updates: Partial<AppSettings>) => request<AppSettings>('PUT', '/settings', updates),
};

// ── Waiting list ─────────────────────────────────────────────────────────────

export const waitingList = {
  getAll: () => request<WaitingEntry[]>('GET', '/waiting-list'),
  add: (fullName: string, source?: WaitingEntry['source'], tableId?: number | null) =>
    request<WaitingEntry[]>('POST', '/waiting-list', { fullName, source, tableId }),
  remove: (id: number) => request<WaitingEntry[]>('DELETE', `/waiting-list/${id}`),
  markPlaying: (id: number) => request<WaitingEntry[]>('PUT', `/waiting-list/${id}/playing`),
  moveUp: (id: number) => request<WaitingEntry[]>('PUT', `/waiting-list/${id}/up`),
  moveDown: (id: number) => request<WaitingEntry[]>('PUT', `/waiting-list/${id}/down`),
  clear: () => request<{ ok: boolean }>('DELETE', '/waiting-list'),
};

// ── Backup ───────────────────────────────────────────────────────────────────

export const backup = {
  download: async (): Promise<void> => {
    const token = getToken();
    const res = await fetch('/api/backup', {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    if (!res.ok) throw new Error('Backup download failed');
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    const date = new Date().toISOString().slice(0, 10);
    a.download = `cuetitan-backup-${date}.db`;
    a.click();
    URL.revokeObjectURL(url);
  },

  restore: async (file: File): Promise<void> => {
    const token = getToken();
    const buf = await file.arrayBuffer();
    const res = await fetch('/api/backup', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/octet-stream',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: buf,
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({ error: res.statusText }));
      throw new Error((err as { error?: string }).error || res.statusText);
    }
  },
};

// ── License ──────────────────────────────────────────────────────────────────

export type LicenseStatus =
  | { status: 'unlicensed' }
  | { status: 'invalid' }
  | { status: 'expired'; type: 'annual'; clubName: string; expiresAt: string }
  | { status: 'active'; type: 'annual' | 'lifetime'; clubName: string; issuedAt: string; expiresAt: string | null; daysRemaining: number | null };

export const license = {
  getStatus: () => request<LicenseStatus>('GET', '/license/status'),
  activate: (key: string) =>
    request<{ ok: boolean; license: LicenseStatus }>('POST', '/license/activate', { key }),
};

// ── Tournaments ────────────────────────────────────────────────────────────

export const tournaments = {
  getAll: () => request<Tournament[]>('GET', '/tournaments'),
  getById: (id: number) => request<TournamentDetail>('GET', `/tournaments/${id}`),
  create: (data: { name: string; gameType: GameType; format: TournamentFormat; racksPerMatch: number; knockoutFromTop?: number }) =>
    request<Tournament>('POST', '/tournaments', data),
  delete: (id: number) => request<{ ok: boolean }>('DELETE', `/tournaments/${id}`),
  addPlayer: (id: number, name: string, seed?: number | null) =>
    request<{ id: number; name: string }>('POST', `/tournaments/${id}/players`, { name, seed }),
  removePlayer: (tournamentId: number, playerId: number) =>
    request<{ ok: boolean }>('DELETE', `/tournaments/${tournamentId}/players/${playerId}`),
  generate: (id: number) => request<TournamentDetail>('POST', `/tournaments/${id}/generate`),
  generateKnockout: (id: number) => request<TournamentDetail>('POST', `/tournaments/${id}/knockout`),
  recordResult: (
    tournamentId: number,
    matchId: number,
    data: { player1Score: number; player2Score: number; winnerId: number; tableId?: number | null }
  ) => request<TournamentDetail>('PUT', `/tournaments/${tournamentId}/matches/${matchId}/result`, data),
  assignTable: (tournamentId: number, matchId: number, tableId: number | null) =>
    request<{ ok: boolean }>('PUT', `/tournaments/${tournamentId}/matches/${matchId}/table`, { tableId }),
};

// ── Dynamic Pricing ─────────────────────────────────────────────────────────
export const pricing = {
  getAll: () => request<PricingSlot[]>('GET', '/pricing'),
  create: (data: Omit<PricingSlot, 'id'>) => request<PricingSlot>('POST', '/pricing', data),
  update: (id: number, data: Partial<Omit<PricingSlot, 'id'>>) => request<PricingSlot>('PUT', `/pricing/${id}`, data),
  delete: (id: number) => request<{ ok: boolean }>('DELETE', `/pricing/${id}`),
};

// ── Session (unused directly but typed for reference) ───────────────────────
export type { Session, Table, TableStatus, TableType, Tournament, TournamentDetail, TournamentFormat };
